/**
 * Procedural audio — every sound is synthesized here, from oscillators
 * and generated noise. Nothing is sampled from any existing game, even as
 * a temporary placeholder (Iron Rule 7a). "Placeholder ART is fine;
 * placeholder FEEL is not" — so the audio is real from the first build,
 * it just is not recorded.
 *
 * Render-layer only: nothing here is visible to the sim.
 */
import type { JuiceConfig } from './juice.js';

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private chain = 0;
  private lastPickupAt = -1e9;
  private lastShotPitch = 1;
  private now = 0;
  private muted = false;
  private pending: number[] = [];

  constructor(private readonly cfg: JuiceConfig) {}

  /** Must be called from a user gesture — browsers block autoplay. */
  resume(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this.cfg.audio.masterGain;
      // F1's own spec text: "voice limiting ... so 300 bodies never clip
      // the mix." Every tone()/burst() call spawns its own oscillator with
      // no cap on concurrent count, so a dense wave clear (dozens of
      // simultaneous Death events) can stack that many full-gain voices
      // into one bus. A limiter on the shared bus is the cheap backstop —
      // it can't fix a mix that's already too busy to read, but it stops
      // that busyness from clipping into distortion.
      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 6;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.15;
      this.master.connect(limiter);
      limiter.connect(this.ctx.destination);

      const len = Math.floor(this.ctx.sampleRate * 0.4);
      this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = this.noise.getChannelData(0);
      let s = 0x2f6e2b1;
      for (let i = 0; i < len; i++) {
        s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s |= 0;
        data[i] = (s / 0x7fffffff) * 0.5;
      }
      this.ambience();
    }
    void this.ctx.resume();
  }

  /**
   * THE SOUNDSCAPE (audit M5). A continuous bed under the SFX: wind over
   * the causeway, the sea washing far below, distant gulls, and a slow
   * two-chord pad so the run has a score rather than a silence between
   * shots. All synthesized (Iron Rule 7a) and routed through `master`, so
   * mute silences the world too. Starts with the first user gesture —
   * exactly when the browser allows sound anyway.
   */
  private amb: GainNode | null = null;
  private ambience(): void {
    if (!this.ctx || !this.master || this.amb) return;
    const ctx = this.ctx;
    const amb = ctx.createGain();
    amb.gain.setValueAtTime(0.0001, ctx.currentTime);
    amb.gain.exponentialRampToValueAtTime(0.9, ctx.currentTime + 4);
    amb.connect(this.master);
    this.amb = amb;

    // A long loop buffer of its own: looping the 0.4s SFX buffer pulses
    // audibly at 2.5Hz, which reads as a helicopter, not weather.
    const len = Math.floor(ctx.sampleRate * 3);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

    // WIND — banded noise with a slow strength wander.
    const wind = ctx.createBufferSource();
    wind.buffer = buf; wind.loop = true;
    const wf = ctx.createBiquadFilter();
    wf.type = 'bandpass'; wf.frequency.value = 420; wf.Q.value = 0.8;
    const wg = ctx.createGain(); wg.gain.value = 0.045;
    wind.connect(wf); wf.connect(wg); wg.connect(amb);
    const wlfo = ctx.createOscillator(); wlfo.frequency.value = 0.06;
    const wlg = ctx.createGain(); wlg.gain.value = 0.022;
    wlfo.connect(wlg); wlg.connect(wg.gain); wlfo.start();
    wind.start();

    // SEA — deep slow wash, swelling on its own period so wind and water
    // never breathe in sync.
    const sea = ctx.createBufferSource();
    sea.buffer = buf; sea.loop = true; sea.playbackRate.value = 0.55;
    const sf = ctx.createBiquadFilter();
    sf.type = 'lowpass'; sf.frequency.value = 260;
    const sg = ctx.createGain(); sg.gain.value = 0.075;
    sea.connect(sf); sf.connect(sg); sg.connect(amb);
    const slfo = ctx.createOscillator(); slfo.frequency.value = 0.085;
    const slg = ctx.createGain(); slg.gain.value = 0.035;
    slfo.connect(slg); slg.connect(sg.gain); slfo.start();
    sea.start();

    // PAD — three soft triangles drifting between two chords (Am -> F),
    // four-second glides, never a hard change. The "score".
    const padG = ctx.createGain(); padG.gain.value = 0.016;
    const pf = ctx.createBiquadFilter();
    pf.type = 'lowpass'; pf.frequency.value = 820;
    padG.connect(pf); pf.connect(amb);
    const chordA = [220, 261.63, 329.63];
    const chordB = [174.61, 220, 261.63];
    const oscs = chordA.map((f) => {
      const o = ctx.createOscillator();
      o.type = 'triangle'; o.frequency.value = f;
      const og = ctx.createGain(); og.gain.value = 0.33;
      o.connect(og); og.connect(padG); o.start();
      return o;
    });
    let onB = false;
    const swap = (): void => {
      if (!this.ctx) return;
      onB = !onB;
      const target = onB ? chordB : chordA;
      for (const [i, o] of oscs.entries()) {
        o.frequency.linearRampToValueAtTime(target[i]!, ctx.currentTime + 4);
      }
      window.setTimeout(swap, 9000 + Math.random() * 7000);
    };
    window.setTimeout(swap, 8000);

    // GULLS — a falling two-note cry, far away, on an irregular clock.
    // Scheduled off `pending` so a run reset never silences the sky.
    const gull = (): void => {
      if (!this.ctx) return;
      const t0 = ctx.currentTime;
      for (const [i, f0] of [1180, 990].entries()) {
        const o = ctx.createOscillator();
        o.type = 'sawtooth';
        o.frequency.setValueAtTime(f0 + Math.random() * 120, t0 + i * 0.22);
        o.frequency.exponentialRampToValueAtTime(f0 * 0.72, t0 + i * 0.22 + 0.18);
        const og = ctx.createGain();
        og.gain.setValueAtTime(0.0001, t0 + i * 0.22);
        og.gain.exponentialRampToValueAtTime(0.012, t0 + i * 0.22 + 0.05);
        og.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.22 + 0.2);
        const of = ctx.createBiquadFilter();
        of.type = 'bandpass'; of.frequency.value = 1400;
        o.connect(of); of.connect(og); og.connect(amb);
        o.start(t0 + i * 0.22); o.stop(t0 + i * 0.22 + 0.25);
      }
      window.setTimeout(gull, 9000 + Math.random() * 12000);
    };
    window.setTimeout(gull, 5000 + Math.random() * 5000);
  }

  /**
   * VOICE LIMIT (F1). Reset once per rendered frame — `frame()` may step
   * the sim, and so call every per-event sound, several times before the
   * next paint, so the budget has to live above that loop, not inside it.
   * Only NORMAL hits are capped: a heavy hit, a death and a gate break are
   * comparatively rare and are exactly the ones that must always be heard,
   * so priority means "never rationed", not "rationed less".
   */
  private normalHitBudget = 0;
  setClock(ms: number): void { this.now = ms; this.normalHitBudget = 6; }

  /** Schedule a note, remembering it so a new run can cancel it. */
  private later(fn: () => void, ms: number): void {
    this.pending.push(window.setTimeout(fn, ms));
  }

  /** Called when a run starts: silence anything the last run queued. */
  reset(): void {
    for (const id of this.pending) window.clearTimeout(id);
    this.pending.length = 0;
    this.chain = 0;
  }

  /** Mute control. Top-grossing titles all ship one; ours had none. */
  setMuted(m: boolean): void {
    // Recorded on the instance, not just applied: muting BEFORE the first
    // gesture used to be a no-op because `master` did not exist yet, so
    // the player muted, played, and the game was loud.
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : this.cfg.audio.masterGain;
  }

  private env(node: AudioNode, gain: number, attack: number, decay: number): void {
    if (!this.ctx || !this.master) return;
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    node.connect(g);
    g.connect(this.master);
  }

  private tone(freq: number, type: OscillatorType, gain: number, attack: number, decay: number, sweepTo?: number): void {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    o.type = type;
    const t = this.ctx.currentTime;
    o.frequency.setValueAtTime(freq, t);
    if (sweepTo !== undefined) o.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t + attack + decay);
    this.env(o, gain, attack, decay);
    o.start(t);
    o.stop(t + attack + decay + 0.02);
  }

  private burst(gain: number, decay: number, filterHz: number): void {
    if (!this.ctx || !this.noise) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = filterHz;
    src.connect(f);
    this.env(f, gain, 0.004, decay);
    src.start(this.ctx.currentTime);
    src.stop(this.ctx.currentTime + decay + 0.05);
  }

  /** B1: layered, with pitch variance — never the same sound twice in a row. */
  shoot(): void {
    if (!this.ctx) return;
    const v = this.cfg.audio.shootPitchVariance;
    let pitch = 1 + (Math.random() * 2 - 1) * v;
    // Explicitly avoid repeating the previous pitch.
    if (Math.abs(pitch - this.lastShotPitch) < v * 0.35) pitch += v * 0.5;
    this.lastShotPitch = pitch;
    this.tone(560 * pitch, 'square', 0.10, 0.004, 0.055, 320 * pitch);
    this.burst(0.05, 0.04, 1900 * pitch);
  }

  /**
   * B2/F1. Never a flat "1 sound" — pitch and gain jitter per instance so a
   * volley does not read as one impact looped, and only the volley's
   * OVERFLOW gets rationed, never a heavy hit.
   */
  hit(heavy: boolean): void {
    if (!heavy) {
      if (this.normalHitBudget <= 0) return;
      this.normalHitBudget--;
    }
    const pj = 1 + (Math.random() * 2 - 1) * 0.14;
    const gj = 0.82 + Math.random() * 0.36;
    this.burst((heavy ? 0.13 : 0.06) * gj, heavy ? 0.07 : 0.035, (heavy ? 850 : 1500) * pj);
    if (heavy) this.tone(150 * pj, 'triangle', 0.10 * gj, 0.004, 0.10, 70 * pj);
  }

  /** B3: the pickup chain rises in pitch — the genre's best audio feature. */
  pickup(): void {
    if (this.now - this.lastPickupAt > this.cfg.audio.pickupChainResetMs) this.chain = 0;
    this.lastPickupAt = this.now;
    this.chain = Math.min(this.chain + 1, this.cfg.audio.pickupChainMax);
    // Equal-temperament step per link, without Math.pow in a hot path.
    let mult = 1;
    for (let i = 0; i < this.chain; i++) mult *= 1.0595;
    this.tone(430 * mult, 'sine', 0.07, 0.003, 0.09, 640 * mult);
  }

  /** Never voice-limited — a death is rare enough to always land. */
  death(): void {
    const pj = 1 + (Math.random() * 2 - 1) * 0.10;
    const gj = 0.85 + Math.random() * 0.3;
    this.burst(0.10 * gj, 0.09, 700 * pj);
    this.tone(240 * pj, 'sawtooth', 0.06 * gj, 0.003, 0.11, 90 * pj);
  }

  /** R5: a shootable panel (crate/barrier/vehicle) breaks open under fire. */
  gateBreak(): void {
    this.burst(0.14, 0.08, 2200);
    this.tone(340, 'square', 0.09, 0.003, 0.09, 120);
  }

  squadHit(): void {
    this.tone(90, 'square', 0.16, 0.004, 0.19, 42);
    this.burst(0.10, 0.10, 380);
  }

  /**
   * A boss swing that costs several soldiers in one hit needs to sound
   * bigger than routine chip damage (squadHit()) -- same proportional-
   * reaction rule as massKill(), applied to the squad's own losses
   * instead of the horde's. Lower and dirtier, layered with body count.
   */
  heavySquadHit(soldiersLost: number): void {
    const voices = Math.min(4, Math.max(1, Math.round(soldiersLost)));
    this.tone(64, 'sawtooth', 0.20, 0.006, 0.24, 36);
    for (let i = 0; i < voices; i++) {
      this.later(() => this.tone(70 - i * 8, 'square', 0.10, 0.003, 0.12, 40), i * 40);
    }
    this.burst(0.16, 0.14, 300);
  }

  /** B5: count-up arpeggio, so the multiplication is heard as well as seen. */
  gate(good: boolean, steps: number): void {
    const n = Math.max(1, Math.min(10, steps));
    for (let i = 0; i < n; i++) {
      const base = good ? 480 : 300;
      let mult = 1;
      for (let k = 0; k < i; k++) mult *= good ? 1.14 : 0.90;
      this.later(() => this.tone(base * mult, 'triangle', 0.09, 0.003, 0.07), i * 42);
    }
  }

  /** B6 */
  levelUp(): void {
    for (const [i, f] of [523, 659, 784, 1046].entries()) {
      this.later(() => this.tone(f, 'triangle', 0.09, 0.006, 0.28), i * 55);
    }
  }

  /** B7 */
  bossIntro(): void {
    this.tone(58, 'sawtooth', 0.22, 0.05, 1.1, 34);
    this.burst(0.16, 0.7, 200);
  }

  bossDeath(): void {
    this.tone(70, 'sawtooth', 0.24, 0.02, 1.4, 28);
    this.burst(0.2, 1.0, 320);
    for (const [i, f] of [392, 523, 659, 784, 1046, 1318].entries()) {
      this.later(() => this.tone(f, 'triangle', 0.10, 0.005, 0.45), 120 + i * 70);
    }
  }

  /** B7: phase crossing — the roar drops a fifth and gets dirtier. */
  bossPhase(phase: number): void {
    const base = phase >= 2 ? 44 : 54;
    this.tone(base, 'sawtooth', 0.24, 0.03, 0.9, base * 0.55);
    this.tone(base * 3, 'square', 0.08, 0.02, 0.5, base * 1.4);
    this.burst(0.18, 0.5, 260);
  }

  /**
   * C8/I10: a new body joining the squad should sound like growth, not
   * like the gear-change chime of weaponSwap() -- a warm rising two-note
   * sine (weaponSwap is a bright three-note square) so a recruit pickup
   * is aurally distinct with the screen off.
   */
  recruit(): void {
    for (const [i, f] of [392, 523].entries()) {
      this.later(() => this.tone(f, 'sine', 0.08, 0.005, 0.17), i * 60);
    }
    this.burst(0.05, 0.07, 1100);
  }

  /**
   * D6: one collective cue for a mass-death moment, in place of N
   * overlapping death() calls stacking into noise. Pitch and voice count
   * scale with how many died at once (capped), so a 5-kill clear sounds
   * bigger than a 5-kill trickle without adding a new instrument.
   */
  massKill(count: number): void {
    const voices = Math.min(5, 2 + Math.floor(count / 4));
    for (let i = 0; i < voices; i++) {
      const f = 180 - i * 14;
      this.later(() => this.tone(f, 'sawtooth', 0.09, 0.004, 0.16, 60), i * 30);
    }
    this.burst(0.13, 0.16, 500);
  }

  /**
   * A-43: the dash reads as MOTION, not impact -- the one cue in this file
   * with no percussive tone underneath. A rising pitch sweep (sine, swept
   * low to high via `tone`'s sweepTo) plus a bright filtered-noise rush.
   */
  dodge(): void {
    this.tone(180, 'sine', 0.10, 0.02, 0.18, 900);
    this.burst(0.07, 0.12, 2600);
  }

  /** A weapon swap should be audible, not just visible. */
  weaponSwap(): void {
    for (const [i, f] of [660, 880, 1320].entries()) {
      this.later(() => this.tone(f, 'square', 0.07, 0.004, 0.13), i * 45);
    }
    this.burst(0.06, 0.09, 2400);
  }

  /** The moment a level-up choice lands. */
  choicePick(): void {
    this.tone(880, 'triangle', 0.11, 0.004, 0.22, 1500);
    this.burst(0.09, 0.14, 1200);
  }

  /** B8 */
  heartbeat(): void {
    this.tone(62, 'sine', 0.16, 0.012, 0.16, 44);
    this.later(() => this.tone(52, 'sine', 0.11, 0.012, 0.19, 38), 150);
  }
}
